<script lang="ts">
	import { formatMoney, inDollars } from '$lib/utils/money';
	import { getPromoOffAmount } from '$lib/utils/stripe';
	import type { Stripe } from 'stripe';
	import { fade, fly } from 'svelte/transition';
	import { invalidateAllPrices } from '$lib/state/reactiveProduct.svelte';
	import { shoppingCart } from '$lib/state/shoppingCart.svelte';
	import type { HTMLAttributes } from 'svelte/elements';

	interface Props extends HTMLAttributes<HTMLDivElement> {
		price: number;
		promoCode?: Stripe.PromotionCode;
		isSpecialPromo?: boolean;
		interval?: Stripe.Price.Recurring.Interval;
	}

	let {
		price,
		promoCode,
		isSpecialPromo = false,
		interval,
		class: className,
		...other
	}: Props = $props();

	let amountOff = $derived(promoCode?.coupon ? getPromoOffAmount(price, promoCode?.coupon) : 0);
	let activeCode = $derived(amountOff > 0);
</script>

<div
	class={['flex items-center gap-x-2', className]}
	{...other}
	in:fade
	itemprop="offers"
	itemscope
	itemtype="https://schema.org/Offer"
>
	<meta itemprop="priceCurrency" content="USD" />
	{#if interval === 'month'}
		<p>{formatMoney(inDollars(price))} / mo + tax</p>
	{:else}
		<div class="flex items-center gap-x-2">
			{#if activeCode}
				{@const discountedPrice = inDollars(Math.max(price - amountOff, 0))}
				<p class="text-green-3" transition:fly={{ x: '-25%' }}>
					<span itemprop="price" content={discountedPrice.toFixed(2)}
						>{formatMoney(discountedPrice)}</span
					>
				</p>
			{/if}

			<p class="text-brown-1" class:line-through={activeCode} class:text-[#7C6E67]={activeCode}>
				<span {...activeCode ? {} : { itemprop: 'price', content: inDollars(price).toFixed(2) }}
					>{formatMoney(inDollars(price))}</span
				>
			</p>
		</div>
	{/if}

	{#if isSpecialPromo && promoCode}
		<button
			type="button"
			class="rounded-s bg-[#F5F5F0] px-3 py-[9px] text-[14px]/[100%] font-semibold uppercase tracking-[-.56px] text-brown-2"
			onclick={async () => {
				await fetch('/api/couponCode', {
					method: 'POST',
					body: JSON.stringify({ couponCode: promoCode.code })
				});
				invalidateAllPrices();
				shoppingCart.open();
			}}
			transition:fly={{ x: '50%' }}
			>{formatMoney(inDollars(amountOff))} off with<br /> code
			<u class="underline">{promoCode.code}</u></button
		>
	{/if}
</div>
